import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { PhoneFrame, BrandAvatar } from './styles';
import { hasMediaContent, isImage, isVideo, truncate } from './utils';
import MediaSlide from './MediaSlide';

const LINKEDIN_FONT = '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const TRUNCATE_AT = 3000;
const TEXT_GREY = 'rgba(0, 0, 0, 0.6)';

const Card = styled(PhoneFrame)({
    fontFamily: LINKEDIN_FONT,
    color: 'rgba(0, 0, 0, 0.9)',
    padding: '12px 16px',
});

const Header = styled('div')({ display: 'flex', gap: 8, alignItems: 'flex-start' });

const Identity = styled('div')({ flex: 1, minWidth: 0 });

const DisplayName = styled('div')({
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '18px',
    color: 'rgba(0, 0, 0, 0.9)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const SubLine = styled('div')({
    fontSize: 12,
    lineHeight: '16px',
    color: TEXT_GREY,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const More = styled(MoreHorizIcon)({ color: TEXT_GREY, fontSize: 20 });

const Body = styled('div')({
    fontSize: 14,
    lineHeight: '20px',
    color: 'rgba(0, 0, 0, 0.9)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: 8,
});

const MediaWrap = styled('div')({
    marginTop: 10,
    marginLeft: -16,
    marginRight: -16,
    overflow: 'hidden',
});

const Grid = styled('div', { shouldForwardProp: p => p !== 'count' })(({ count }) => ({
    display: 'grid',
    gap: 2,
    aspectRatio: '16 / 9',
    gridTemplateColumns: count === 1 ? '1fr' : '1fr 1fr',
    gridTemplateRows: count > 2 ? '1fr 1fr' : '1fr',
    ...(count === 3 && { gridTemplateAreas: '"a b" "a c"' }),
}));

const Tile = styled('img', { shouldForwardProp: p => p !== 'area' })(({ area }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    ...(area && { gridArea: area }),
}));

const Divider = styled('div')({ height: 1, background: 'rgba(0, 0, 0, 0.08)', margin: '8px 0 4px' });

const ActionsRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    color: TEXT_GREY,
});

const Action = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    padding: '6px 4px',
    '& svg': { fontSize: 18 },
});

const LinkedInMedia = ({ media }) => {
    const items = useMemo(
        () => (Array.isArray(media) ? media : []).filter(m => m?.link && (isImage(m) || isVideo(m))),
        [media],
    );

    if (items.length === 0) return null;

    // A lone item or any video renders on its own; multiple images tile like the LinkedIn feed.
    if (items.length === 1 || isVideo(items[0])) {
        return (
            <MediaWrap>
                <MediaSlide media={[items[0]]} aspectRatio="16 / 9" />
            </MediaWrap>
        );
    }

    const images = items.filter(isImage).slice(0, 4);
    const areas = images.length === 3 ? ['a', 'b', 'c'] : [];

    return (
        <MediaWrap>
            <Grid count={images.length}>
                {images.map((item, i) => (
                    <Tile key={`${item.link}-${i}`} src={item.link} alt="" area={areas[i]} />
                ))}
            </Grid>
        </MediaWrap>
    );
};

const PlatformCardLinkedIn = ({ pageName, locationLogo, message, media, dateLabel }) => {
    const { shown } = truncate(message, TRUNCATE_AT);

    return (
        <Card>
            <Header>
                <BrandAvatar name={pageName} image={locationLogo} size={48} color="#0A66C2" />
                <Identity>
                    <DisplayName>{pageName}</DisplayName>
                    {dateLabel && <SubLine>{dateLabel} · 🌐</SubLine>}
                </Identity>
                <More />
            </Header>

            {message && <Body>{shown}</Body>}

            {hasMediaContent(media) && <LinkedInMedia media={media} />}

            <Divider />
            <ActionsRow>
                <Action>
                    <ThumbUpAltOutlinedIcon /> Like
                </Action>
                <Action>
                    <ChatBubbleOutlineIcon /> Comment
                </Action>
                <Action>
                    <RepeatIcon /> Repost
                </Action>
                <Action>
                    <SendOutlinedIcon /> Send
                </Action>
            </ActionsRow>
        </Card>
    );
};

export default PlatformCardLinkedIn;
